/**
 * Bonsai OS dual-tier age restriction (COPPA Tier 0 + adult Tier 1).
 *
 * Tier 0 — platform entry: self-attest 13+ + Terms (localStorage per origin).
 * Tier 1 — adult/mature: 21+ (on-chain where available: Registry / Tube).
 *
 * Each canister origin needs its own Tier 0 accept (localStorage is not shared).
 */

export const PLATFORM_AGE_MIN = 13;
export const ADULT_AGE_MIN = 21;

/** Bump when Tier 0 legal copy materially changes — forces re-accept. */
export const OS_AGE_POLICY_VERSION = "2026-10-15";

export const OS_AGE_TIER0_KEY = "bonsai_os_age_tier0_v1";
export const OS_AGE_TIER0_DECLINED_KEY = "bonsai_os_age_tier0_declined_v1";

/** localStorage flag for session adult unlock when II/on-chain attest is unavailable. */
export const OS_ADULT_SESSION_KEY = "bonsai_os_adult_session_v1";

export const OS_AGE_GATE_EVENT = "bonsai-os:age-gate";

export type OsAgeGateDetail = {
  accepted: boolean;
  declined: boolean;
  policyVersion: string;
};

export type OsAgeTier0Record = {
  v: string;
  acceptedAt: number;
  minAge: typeof PLATFORM_AGE_MIN;
};

export const PLATFORM_AGE_TERMS = `You must be ${PLATFORM_AGE_MIN} years of age or older to use Bonsai OS (Hub, Registry, Radio, Cloud, Works, BonsaiTV, Bazaar, Canopy, Crossgrove).

By continuing you confirm you meet this age requirement and agree to the Bonsai OS Terms of Service and Privacy Policy. Parents and guardians: do not allow children under ${PLATFORM_AGE_MIN} to use these services. We do not knowingly collect personal information from children under ${PLATFORM_AGE_MIN}.

Illegal content (including child sexual exploitation) is prohibited and will be removed and reported when required by law.`;

export const ADULT_CONTENT_TERMS = `You must be ${ADULT_AGE_MIN} years of age or older to view or submit adult-oriented or Mature-rated material on Bonsai Registry, Bonsai Bazaar, Bonsai Radio, and BonsaiTV.

Adult and Mature content is hidden by default (Safe Mode / browse-safe). Bonsai platforms may link to outbound content; you access it at your own risk and warrant compliance with applicable law.

Illegal content (including child exploitation, trafficking, and illegal narcotics promotion) is prohibited and will be rejected.`;

export const PARENTAL_NOTICE =
  "If you are a parent or guardian and believe a minor under 13 has accessed Bonsai OS, leave this site and contact us via the Privacy Policy contact path. Do not create accounts for children under 13.";

function readTier0(): OsAgeTier0Record | null {
  try {
    const raw = localStorage.getItem(OS_AGE_TIER0_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OsAgeTier0Record;
    if (!parsed || typeof parsed.v !== "string") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** True when this origin has accepted the current Tier 0 policy version. */
export function hasAcceptedPlatformAgeGate(): boolean {
  const rec = readTier0();
  return !!rec && rec.v === OS_AGE_POLICY_VERSION && rec.minAge === PLATFORM_AGE_MIN;
}

export function hasDeclinedPlatformAgeGate(): boolean {
  try {
    return localStorage.getItem(OS_AGE_TIER0_DECLINED_KEY) === OS_AGE_POLICY_VERSION;
  } catch {
    return false;
  }
}

export function acceptPlatformAgeGate(): void {
  const rec: OsAgeTier0Record = {
    v: OS_AGE_POLICY_VERSION,
    acceptedAt: Date.now(),
    minAge: PLATFORM_AGE_MIN,
  };
  try {
    localStorage.setItem(OS_AGE_TIER0_KEY, JSON.stringify(rec));
    localStorage.removeItem(OS_AGE_TIER0_DECLINED_KEY);
  } catch {
    /* ignore */
  }
  dispatchAgeGate({ accepted: true, declined: false, policyVersion: OS_AGE_POLICY_VERSION });
}

export function declinePlatformAgeGate(): void {
  try {
    localStorage.setItem(OS_AGE_TIER0_DECLINED_KEY, OS_AGE_POLICY_VERSION);
    localStorage.removeItem(OS_AGE_TIER0_KEY);
  } catch {
    /* ignore */
  }
  dispatchAgeGate({ accepted: false, declined: true, policyVersion: OS_AGE_POLICY_VERSION });
}

/** Clear decline so the user can re-open the gate (e.g. after Safe Exit). */
export function clearPlatformAgeDecline(): void {
  try {
    localStorage.removeItem(OS_AGE_TIER0_DECLINED_KEY);
  } catch {
    /* ignore */
  }
}

export function hasAdultSessionUnlock(): boolean {
  try {
    const raw = localStorage.getItem(OS_ADULT_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { v?: string };
    return parsed?.v === OS_AGE_POLICY_VERSION;
  } catch {
    return false;
  }
}

export function setAdultSessionUnlock(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.setItem(
        OS_ADULT_SESSION_KEY,
        JSON.stringify({ v: OS_AGE_POLICY_VERSION, at: Date.now() }),
      );
    } else {
      localStorage.removeItem(OS_ADULT_SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

function dispatchAgeGate(detail: OsAgeGateDetail): void {
  try {
    window.dispatchEvent(new CustomEvent<OsAgeGateDetail>(OS_AGE_GATE_EVENT, { detail }));
  } catch {
    /* ignore */
  }
}

export const DEFAULT_TERMS_URL =
  "https://u2jsa-baaaa-aaaau-aghea-cai.icp0.io/legal/terms";
export const DEFAULT_PRIVACY_URL =
  "https://u2jsa-baaaa-aaaau-aghea-cai.icp0.io/legal/privacy";
